import { Controller } from '@hotwired/stimulus'
import {
  setDecimals,
  decimalsInValue
} from '../../../../javascript/utils/math_helpers'

export default class extends Controller {
  static targets = [
    'hourCheckbox',
    'hourLock',
    'hourVolume',
    'hourLockIcon',
    'quarterCheckbox',
    'quarterLock',
    'quarterVolume',
    'quarterLockIcon'
  ]
  static values = { decimalCount: Number }

  hourLockTargetConnected(element) {
    const hoursIndex =
      element.dataset['kaia-ManualForecast-TableComponent-HoursIndexParam']
    const targetLockClass = this.#getHourLockIconsFromQuarters(hoursIndex)
    if (targetLockClass) this.#applyLockClass(targetLockClass, hoursIndex)
  }

  toggle(event) {
    this.#toggleQuartersAtIndex(event.params.index, event.target.checked)
  }

  lock(event) {
    this.#toggleQuarterLocksAtIndex(event.params.index, event.target.checked)
  }

  toggleHourWithQuarters(hourIndex, isChecked) {
    this.#toggleCheckbox(this.hourCheckboxTargets[hourIndex], isChecked)
    this.#toggleQuartersAtIndex(hourIndex, isChecked)
  }

  toggleLocksWithQuarters(hourIndex, isChecked) {
    this.#toggleCheckbox(this.hourLockTargets[hourIndex], isChecked)
    this.#toggleQuarterLocksAtIndex(hourIndex, isChecked)
  }

  handleInput(event) {
    if (
      event.target.value !== '' &&
      !Number.isFinite(parseFloat(event.target.value))
    )
      return // don't perform calculations on non-number (we regard empty string as 0)

    event.target.value = this.#formattedInputValue(event.target.value)
    const [inputIsQuarterly, inputIndex] = [
      event.params.isQuarterly,
      event.params.index
    ]
    inputIsQuarterly
      ? this.#addQuarterVolumesAverageToHour(inputIndex)
      : this.#addHourVolumeToQuarters(inputIndex)
  }

  handleLockingIcons(event) {
    const [inputIsQuarterly, inputIndex] = [
      event.params.isQuarterly,
      event.params.index
    ]
    if (!inputIsQuarterly) return

    const targetLockClass = this.#getHourLockIconsFromQuarters(inputIndex)
    this.#applyLockClass(targetLockClass, inputIndex)
  }

  #applyLockClass(targetLockClass, index) {
    if (targetLockClass) {
      const fourLocks = this.hourLockIconTargets[index].children
      for (const item of fourLocks) {
        if (item.classList.contains(targetLockClass))
          item.classList.remove('hidden')
        else item.classList.add('hidden')
      }
      this.hourLockIconTargets[index].classList.remove('invisible')
      this.hourLockIconTargets[index].classList.remove('mt-5')
    } else this.hourLockIconTargets[index].classList.add('invisible')
  }

  #getHourLockIconsFromQuarters(index) {
    const quarterLockCount = this.#quarterLocksAtIndex(parseInt(index))
      .map((input) => input.checked)
      .filter((x) => x == true).length
    if (quarterLockCount == 0) return
    else return 'quarter_' + quarterLockCount
  }

  handleBlur(event) {
    if (isNaN(event.target.value)) return (event.target.value = '') // reset input value and return if it formats to NaN
    if (!event.target.value) return

    event.target.value = setDecimals(event.target.value, this.decimalCountValue)
  }

  #formattedInputValue(inputValue) {
    inputValue = inputValue.replace(/,/, '.') // replace comma with period so we can round the value later
    const exceedsMaxDecimals =
      decimalsInValue(inputValue) > this.decimalCountValue
    if (exceedsMaxDecimals)
      inputValue = setDecimals(inputValue, this.decimalCountValue)
    return inputValue
  }

  #addQuarterVolumesAverageToHour(index) {
    const quarterVolumes = this.#quarterVolumesAtIndex(index)
      .map((input) => input.value)
      .filter((volume) => volume)
    if (!quarterVolumes.length)
      return (this.hourVolumeTargets[index].value = '') // empty hour input and return when no defined quarter volumes

    const sumOfQuarterVolumes = quarterVolumes.reduce((a, b) => {
      return a + (parseFloat(b) || 0) // default to 0 if undefined volume
    }, 0)
    this.hourVolumeTargets[index].value = setDecimals(
      sumOfQuarterVolumes / 4,
      this.decimalCountValue
    )
  }

  #addHourVolumeToQuarters(index) {
    const hourInputValue = this.hourVolumeTargets[index].value
    const quarterInputsValue = hourInputValue
      ? setDecimals(hourInputValue, this.decimalCountValue)
      : ''
    this.#quarterVolumesAtIndex(index).forEach(
      (quarterVolume) => (quarterVolume.value = quarterInputsValue)
    )
  }

  #toggleCheckbox(checkbox, isChecked) {
    const isNewCheckedValue = checkbox.checked !== isChecked
    if (!isNewCheckedValue) return

    checkbox.checked = isChecked
    checkbox.dispatchEvent(new Event('change')) // dispatch change event to fire listeners
  }

  #toggleQuartersAtIndex(hourIndex, isChecked) {
    this.#targetsAtIndex(this.quarterCheckboxTargets, hourIndex).forEach(
      (quarterCheckbox) => {
        this.#toggleCheckbox(quarterCheckbox, isChecked)
      }
    )
  }

  #toggleQuarterLocksAtIndex(hourIndex, isChecked) {
    this.#targetsAtIndex(this.quarterLockTargets, hourIndex).forEach(
      (quarterLock) => {
        this.#toggleCheckbox(quarterLock, isChecked)
      }
    )
  }

  #quarterVolumesAtIndex(index) {
    return this.#targetsAtIndex(this.quarterVolumeTargets, index)
  }

  #quarterLocksAtIndex(index) {
    return this.#targetsAtIndex(this.quarterLockTargets, index)
  }

  #targetsAtIndex(targets, index) {
    return targets.filter((target) => {
      const targetIndex = parseInt(
        target.dataset['kaia-ManualForecast-TableComponent-HoursIndexParam']
      )
      return targetIndex === index
    })
  }

  get rowLength() {
    return this.hourVolumeTargets.length
  }
}
